import { HttpErrorResponse } from '@angular/common/http';
import { FtpServerType } from 'src/app/domainObjects/dataConnector';

/** An organization FTP service as returned by the API — never includes the password. */
export interface OrgFtpService {
  id: number;
  ftpName: string;
  ftpServerType: FtpServerType;
  ftpServerLocation: string;
  controlPort: number;
  /** Seconds; the API fills in the default when none is configured. */
  connectionTimeout: number;
  userName: string;
  isActive: number;
  hasPassword: boolean;
}

/** Body for insert-org-ftpservice / update-org-ftpservice. The server sets orgId from the session. */
export interface OrgFtpServiceSaveRequest {
  id?: number;
  ftpName: string;
  ftpServerType: FtpServerType;
  ftpServerLocation: string;
  controlPort: number;
  connectionTimeout: number;
  userName: string;
  /** On update, an empty string keeps the stored password. */
  password: string;
  isActive: number;
}

export interface OrgFtpServiceSaveResponse {
  status: boolean;
  id: number;
  message: string;
}

export interface OrgFtpServiceTestResult {
  /** False when the test couldn't run (test service busy/unreachable); success is then false too. */
  tested: boolean;
  success: boolean;
  message: string;
}

/** A folder on an FTP service (choosing a Data Writer export directory). */
export interface OrgFtpFolderNode {
  name: string;
  path: string;
  children: OrgFtpFolderNode[];
}

export interface OrgFtpFolderBrowseResult {
  items: OrgFtpFolderNode[];
  errorMessage?: string;
}

export type FtpConnectionState = 'testing' | 'connected' | 'failed' | 'not-tested';

export interface FtpConnectionResult {
  state: FtpConnectionState;
  message: string;
}

/** Default control port per server type, used to pre-fill and auto-switch the Port field. */
export const FTP_DEFAULT_PORTS: Record<FtpServerType, number> = {
  [FtpServerType.FTP]: 21,
  [FtpServerType.SFTP]: 22
};

/** Connection timeout (seconds) default and limits — mirror OrgFtpServiceDefaults in the API. */
export const FTP_CONNECTION_TIMEOUT = { default: 30, min: 5, max: 300 };

/** Field lengths mirror the dbo.tbl_FTPSetup column sizes (and the server's request validation). */
export const FTP_FIELD_MAX_LENGTH = {
  ftpName: 200,
  ftpServerLocation: 100,
  userName: 50,
  password: 50
};

export interface FtpApiError {
  message: string;
  /** Field-level messages keyed by form control name, from ASP.NET model validation. */
  fieldErrors: Record<string, string>;
}

/**
 * Turns an API failure into a message for the Analyst. Handles the shapes this API
 * returns: `{ message }` from the FTP endpoints, ASP.NET ValidationProblemDetails
 * (`{ errors: { Field: [..] } }`) from model validation, and transport failures.
 */
export function toFtpApiError(err: unknown): FtpApiError {
  const fieldErrors: Record<string, string> = {};
  if (!(err instanceof HttpErrorResponse)) {
    return { message: 'An unexpected error occurred. Please try again.', fieldErrors };
  }
  if (err.status === 0) {
    return { message: 'Unable to reach the server. Check your connection and try again.', fieldErrors };
  }

  const body = err.error;
  if (body?.errors && typeof body.errors === 'object') {
    for (const [key, messages] of Object.entries(body.errors)) {
      const text = Array.isArray(messages) ? messages.join(' ') : String(messages);
      // "FtpName" / "$.controlPort" -> "ftpName" / "controlPort"
      const name = key.replace(/^\$\./, '');
      fieldErrors[name.charAt(0).toLowerCase() + name.slice(1)] = text;
    }
    return { message: 'Please correct the highlighted fields.', fieldErrors };
  }
  if (typeof body?.message === 'string' && body.message) {
    return { message: body.message, fieldErrors };
  }
  if (err.status === 401) {
    return { message: 'Your session has expired. Please log in again.', fieldErrors };
  }
  if (err.status === 403) {
    return { message: 'You do not have permission to manage FTP services.', fieldErrors };
  }
  return { message: `The request failed (HTTP ${err.status}). Please try again.`, fieldErrors };
}
